import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import { orderApiService } from "services/orderApiService";
import { expenseApiService } from "services/expenseApiService";

const Analytics = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [orders, setOrders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (showLoader = false) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      // Fetch orders
      const ordersData = await orderApiService.getAllOrders();
      setOrders(ordersData);

      // Fetch expenses
      const expensesData = await expenseApiService.getAllExpenses();
      setExpenses(expensesData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filterDataByDate = (data, dateField = 'order_date') => {
    if (!startDate && !endDate) return data;

    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    return data.filter((item) => {
      const itemDate = new Date(item[dateField] || item.date || item.order_date);
      if (start && end) {
        return itemDate >= start && itemDate <= end;
      } else if (start) {
        return itemDate >= start;
      } else if (end) {
        return itemDate <= end;
      }
      return true;
    });
  };

  // Calculate all analytics metrics
  const analyticsData = useMemo(() => {
    const filteredOrders = filterDataByDate(orders, 'order_date');
    const filteredExpenses = filterDataByDate(expenses, 'date');

    // Sales calculations
    const totalSales = filteredOrders.reduce(
      (sum, order) => sum + parseFloat(order.total_amount || 0),
      0
    );

    // Expenses calculations
    const totalExpenses = filteredExpenses.reduce(
      (sum, expense) => sum + parseFloat(expense.total || 0),
      0
    );

    // Order calculations
    const totalOrders = filteredOrders.length;
    const completedOrders = filteredOrders.filter(order => order.status === 'completed').length;
    const pendingOrders = filteredOrders.filter(order => order.status === 'pending').length;

    // Profit calculations
    const totalProfit = totalSales - totalExpenses;
    const profitMargin = totalSales > 0 ? (totalProfit / totalSales) * 100 : 0;

    // Items calculations
    const totalOrderItems = filteredOrders.reduce((sum, order) => {
      return (
        sum +
        (order.orderproductdata
          ? order.orderproductdata.reduce(
            (itemSum, item) => itemSum + (item.quantity || 0),
            0
          )
          : 0)
      );
    }, 0);

    // Average calculations
    const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
    const averageExpense = filteredExpenses.length > 0 ? totalExpenses / filteredExpenses.length : 0;

    return {
      totalSales,
      totalExpenses,
      totalOrders,
      totalProfit,
      totalOrderItems,
      completedOrders,
      pendingOrders,
      profitMargin,
      averageOrderValue,
      averageExpense,
      filteredOrders,
      filteredExpenses,
    };
  }, [orders, expenses, startDate, endDate]);

  const {
    totalSales,
    totalExpenses,
    totalOrders,
    totalProfit,
    totalOrderItems,
    completedOrders,
    pendingOrders,
    profitMargin,
    averageOrderValue,
    averageExpense,
    filteredOrders,
    filteredExpenses,
  } = analyticsData;

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const renderOverview = () => (
    <>
      <div className="row">
        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Total Sales</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{formatCurrency(totalSales)}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="fas fa-dollar-sign font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Total Expenses</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{formatCurrency(totalExpenses)}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-money-bill-wave font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Total Profit</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{formatCurrency(totalProfit)}</span>
                  </h2>
                  <small className="text-muted">
                    Margin: {formatPercentage(profitMargin)}
                  </small>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="fas fa-chart-line font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Total Orders</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{totalOrders}</span>
                  </h2>
                  <small className="text-muted">
                    Items: {totalOrderItems}
                  </small>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="fas fa-shopping-cart font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderOrderAnalytics = () => (
    <>
      <div className="row">
        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Total Orders</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{totalOrders}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="fas fa-clipboard-list font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Completed Orders</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{completedOrders}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="fas fa-check-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Pending Orders</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{pendingOrders}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-clock font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Average Order Value</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{formatCurrency(averageOrderValue)}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="fas fa-calculator font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderSalesAnalytics = () => (
    <>
      <div className="row">
        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Total Sales</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{formatCurrency(totalSales)}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="fas fa-chart-bar font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Total Items Sold</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{totalOrderItems}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="fas fa-boxes font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Avg Items per Order</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">
                      {totalOrders > 0 ? (totalOrderItems / totalOrders).toFixed(1) : 0}
                    </span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="fas fa-balance-scale font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Profit Margin</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{formatPercentage(profitMargin)}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-percent font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderExpenseAnalytics = () => (
    <>
      <div className="row">
        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Total Expenses</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{formatCurrency(totalExpenses)}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-danger rounded-circle">
                      <i className="fas fa-file-invoice-dollar font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Expense Entries</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{filteredExpenses.length}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-secondary rounded-circle">
                      <i className="fas fa-list-ol font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Average Expense</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">{formatCurrency(averageExpense)}</span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="fas fa-calculator font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6">
          <div className="card card-h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="text-muted fw-medium mb-3">Expense Ratio</h5>
                  <h2 className="mb-0">
                    <span className="counter-value">
                      {totalSales > 0 ? formatPercentage((totalExpenses / totalSales) * 100) : "0%"}
                    </span>
                  </h2>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-chart-pie font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Analytics" breadcrumbItem="Dashboard" />

        {/* Date Filters and Tabs */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  {/* Date Filters */}
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0">From:</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: "auto" }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0">To:</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: "auto" }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>

                    {(startDate || endDate) && (
                      <button
                        className="btn btn-light"
                        onClick={() => {
                          setStartDate("");
                          setEndDate("");
                        }}
                      >
                        <i className="fas fa-times me-1"></i> Clear Dates
                      </button>
                    )}
                  </div>

                  {/* Analytics Tabs */}
                  <div className="nav nav-pills">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
                        onClick={() => setActiveTab("overview")}
                      >
                        <i className="fas fa-chart-pie me-1"></i> Overview
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "orders" ? "active" : ""}`}
                        onClick={() => setActiveTab("orders")}
                      >
                        <i className="fas fa-shopping-cart me-1"></i> Orders
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "sales" ? "active" : ""}`}
                        onClick={() => setActiveTab("sales")}
                      >
                        <i className="fas fa-chart-bar me-1"></i> Sales
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeTab === "expenses" ? "active" : ""}`}
                        onClick={() => setActiveTab("expenses")}
                      >
                        <i className="fas fa-money-bill-wave me-1"></i> Expenses
                      </button>
                    </li>
                  </div>

                  {/* Refresh Button */}
                  <button
                    className="btn btn-light"
                    onClick={() => fetchData(false)}
                    disabled={refreshing}
                    title="Refresh Analytics"
                  >
                    <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Content */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <h4 className="card-title mb-0">
                    {activeTab === "overview" && "Overview Analytics"}
                    {activeTab === "orders" && "Order Analytics"}
                    {activeTab === "sales" && "Sales Analytics"}
                    {activeTab === "expenses" && "Expense Analytics"}
                  </h4>
                  <small className="text-muted">
                    {startDate || endDate
                      ? `Showing data from ${startDate || "beginning"} to ${endDate || "now"}`
                      : "Showing all data"}
                  </small>
                </div>

                <div className="mt-4">
                  {activeTab === "overview" && renderOverview()}
                  {activeTab === "orders" && renderOrderAnalytics()}
                  {activeTab === "sales" && renderSalesAnalytics()}
                  {activeTab === "expenses" && renderExpenseAnalytics()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;